import { Box, Checkbox, TableCell as MuiTableCell, styled, Table, TableBody, TableHead, TableRow } from '@mui/material';
import { RiArrowGoBackLine } from '@remixicon/react';
import { useEffect, useMemo, useState } from 'react';
import Hint from 'src/components/atoms/Hint';
import StatusBadge from 'src/components/atoms/StatusBadge';
import { AgButton } from 'src/components/ui';
import { useEditingState, useTransitionCallback } from 'src/providers/EditingStateProvider';
import { NotedOnId } from '../CommentsSection';
import { IconButtonWithConfirmationProps } from './IconButtonWithConfirmation';
import NoteRequiredModal from './NoteRequiredModal';

const TableCell = styled(MuiTableCell)(() => ({ padding: 1 }));
type OrderSelection = { __typename?: string; id: number };
type LinkedOrder = { id: number; __typename?: string; status: string; slug?: string | null };

const statusAfterTransition: Partial<Record<string, string>> = {
  SUBMITTED: 'DRAFT',
  SHIPPED: 'SUBMITTED',
  CLOSED: 'SHIPPED',
};

/**
 * Handles reverting linked orders.
 * This is a blend between the IconButtonWithConfirmation and SyncedTransitionModal components.
 */
export default function LinkedRevertButton({
  handleConfirm,
  isVisible,
  actionLabel,
  entityLabel,
  postActionNotes,
  linkedOrders,
  filterLinkedOrders,
  postCommentTo,
  'data-testid': dataTestId,
}: Omit<IconButtonWithConfirmationProps, 'handleConfirm' | 'icon' | 'modalContent'> & {
  handleConfirm: (ordersToTransition: OrderSelection[]) => Promise<void>;
  linkedOrders: LinkedOrder[];
  filterLinkedOrders: (order: LinkedOrder) => boolean;
  postCommentTo: NotedOnId;
  'data-testid'?: string;
}) {
  const [open, setOpen] = React.useState(false);

  const [ordersToTransition, setOrdersToTransition] = useState<OrderSelection[]>([]);

  const filteredOrders = useMemo(() => {
    return linkedOrders.filter(filterLinkedOrders);
  }, [filterLinkedOrders, linkedOrders]);

  useEffect(() => {
    const ordersToTransition = filteredOrders.map((order) => ({ __typename: order.__typename, id: order.id }));

    setOrdersToTransition(ordersToTransition);
  }, [filteredOrders]);

  const transitionRequest = useTransitionCallback(() => {
    setOpen(false);
    return handleConfirm(ordersToTransition);
  });
  const isTransitioning = useEditingState((state) => state.getIsTransitioning());

  if (!isVisible) {
    return null;
  }

  const confirmLabel = ordersToTransition.length > 0 ? `Revert ${ordersToTransition.length + 1} Orders` : actionLabel;

  return (
    <>
      <Hint title={actionLabel}>
        <AgButton
          variant="white"
          shape="rounded"
          onClick={() => {
            setOpen(true);
          }}
          isDisabled={isTransitioning}
          data-testid={dataTestId}
        >
          <RiArrowGoBackLine size={16} />
        </AgButton>
      </Hint>

      <NoteRequiredModal
        headerLabel={`Revert ${entityLabel}`}
        contentText={`Are you sure you want to revert this ${entityLabel} the previous order state? If so, enter relevant notes below.`}
        confirmLabel={confirmLabel}
        confirmTestId={dataTestId ? 'po-revert-confirm-btn' : undefined}
        open={open}
        external={false}
        additionalOptions={
          linkedOrders.length > 0 ? (
            <LinkedRevertTable
              ordersToTransition={ordersToTransition}
              setOrdersToTransition={setOrdersToTransition}
              filteredOrders={filteredOrders}
            />
          ) : null
        }
        postActionNotes={postActionNotes}
        onSubmit={transitionRequest}
        handleClose={() => {
          setOpen(false);
          setOrdersToTransition([]);
        }}
        postCommentTo={postCommentTo}
      />
    </>
  );
}

const LinkedRevertTable = ({
  ordersToTransition,
  setOrdersToTransition,
  filteredOrders,
}: {
  ordersToTransition: OrderSelection[];
  setOrdersToTransition: React.Dispatch<React.SetStateAction<OrderSelection[]>>;
  filteredOrders: LinkedOrder[];
}) => {
  const partialSelection = ordersToTransition.length > 0 && ordersToTransition.length < filteredOrders.length;

  const allSelected = ordersToTransition.length === filteredOrders.length;

  return (
    <Box sx={{ mt: 1 }}>
      <Table>
        <TableHead>
          <TableCell>
            <Checkbox
              indeterminate={partialSelection}
              checked={allSelected && filteredOrders.length > 0}
              onClick={() => {
                if (allSelected) {
                  setOrdersToTransition([]);

                  return;
                }

                setOrdersToTransition(filteredOrders.map((order) => ({ id: order.id, __typename: order.__typename })));
              }}
            />
          </TableCell>
          <TableCell>Order #</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Next Status</TableCell>
        </TableHead>
        <TableBody>
          {filteredOrders.map((order) => (
            <TableRow key={order.id}>
              <TableCell>
                <Checkbox
                  checked={
                    !!ordersToTransition.find(({ id, __typename }) => {
                      return id === order.id && __typename === order.__typename;
                    })
                  }
                  onChange={(e) => {
                    setOrdersToTransition((orders) => {
                      if (e.target.checked) {
                        return [...orders, { __typename: order.__typename, id: order.id }];
                      }

                      return orders.filter(({ id }) => id !== order.id);
                    });
                  }}
                />
              </TableCell>
              <TableCell>
                {order.__typename === 'PurchaseOrder' ? 'Purchase Order' : 'Sales Order'} #{order.slug}
              </TableCell>
              <TableCell>
                <StatusBadge status={order.status} />
              </TableCell>
              <TableCell>
                <StatusBadge status={statusAfterTransition[order.status]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
